import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

interface Command {
  command: string
  output: string | JSX.Element
  timestamp: Date
}

interface CommandHandler {
  output: string | JSX.Element
  clear?: boolean
}

const commands: Record<string, CommandHandler> = {
  help: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Available commands:</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div><span className="text-accent">help</span> - Show this help message</div>
          <div><span className="text-accent">clear</span> - Clear the terminal</div>
          <div><span className="text-accent">ls</span> - List sections</div>
          <div><span className="text-accent">cd [section]</span> - Navigate to section</div>
          <div><span className="text-accent">about</span> - About me</div>
          <div><span className="text-accent">projects</span> - View projects</div>
          <div><span className="text-accent">contact</span> - Contact information</div>
          <div><span className="text-accent">resume</span> - View resume</div>
          <div><span className="text-accent">skills</span> - Show skills</div>
          <div><span className="text-accent">whoami</span> - Who am I?</div>
          <div><span className="text-accent">date</span> - Current date</div>
          <div><span className="text-accent">echo [text]</span> - Echo text</div>
        </div>
      </div>
    ),
  },
  ls: {
    output: (
      <div className="space-y-1">
        <div className="text-accent">Sections:</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>📄 about</div>
          <div>💼 projects</div>
          <div>📝 resume</div>
          <div>📧 contact</div>
        </div>
      </div>
    ),
  },
  about: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Peyton Clark</div>
        <div>Full Stack Developer & Designer</div>
        <div className="mt-2">Passionate developer with expertise in modern web technologies, creating innovative solutions that blend technical excellence with creative design.</div>
        <div className="mt-2">
          <button 
            onClick={() => document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-accent hover:underline"
          >
            → Navigate to About section
          </button>
        </div>
      </div>
    ),
  },
  projects: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Featured Projects:</div>
        <div className="mt-2 space-y-1">
          <div>• E-Commerce Platform</div>
          <div>• Social Media Dashboard</div>
          <div>• Portfolio Website</div>
        </div>
        <div className="mt-2">
          <button 
            onClick={() => document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-accent hover:underline"
          >
            → Navigate to Projects section
          </button>
        </div>
      </div>
    ),
  },
  contact: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Contact Information:</div>
        <div className="mt-2 space-y-1">
          <div>Email: hello@peytonclark.dev</div>
          <div>Location: Available Worldwide</div>
        </div>
        <div className="mt-2">
          <button 
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-accent hover:underline"
          >
            → Navigate to Contact section
          </button>
        </div>
      </div>
    ),
  },
  resume: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Resume Information:</div>
        <div className="mt-2">View my full resume with experience, education, and skills.</div>
        <div className="mt-2">
          <button 
            onClick={() => document.querySelector('#resume')?.scrollIntoView({ behavior: 'smooth' })}
            className="text-accent hover:underline"
          >
            → Navigate to Resume section
          </button>
        </div>
      </div>
    ),
  },
  skills: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Technical Skills:</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>• React</div>
          <div>• TypeScript</div>
          <div>• Node.js</div>
          <div>• Next.js</div>
          <div>• Tailwind CSS</div>
          <div>• Framer Motion</div>
          <div>• Three.js</div>
          <div>• PostgreSQL</div>
        </div>
      </div>
    ),
  },
  whoami: {
    output: (
      <div className="space-y-2">
        <div className="text-accent">Peyton Clark</div>
        <div>Full Stack Developer & Designer</div>
        <div>Building beautiful, functional web experiences</div>
      </div>
    ),
  },
  date: {
    output: new Date().toLocaleString(),
  },
  clear: {
    output: '',
    clear: true,
  },
}

export default function InteractiveTerminal() {
  const [isOpen, setIsOpen] = useState(false)
  const [commandHistory, setCommandHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [currentCommand, setCurrentCommand] = useState('')
  const [outputs, setOutputs] = useState<Command[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    // Add welcome message
    setOutputs([
      {
        command: '',
        output: (
          <div className="space-y-2">
            <div className="text-accent">Welcome to Peyton's Portfolio Terminal</div>
            <div>Type <span className="text-accent">help</span> to see available commands</div>
          </div>
        ),
        timestamp: new Date(),
      },
    ])
  }, [])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const executeCommand = (cmd: string) => {
    const trimmedCmd = cmd.trim().toLowerCase()
    const parts = trimmedCmd.split(' ')
    const baseCommand = parts[0]
    const args = parts.slice(1)

    if (baseCommand === 'cd') {
      const section = args[0]?.toLowerCase()
      const sections: { [key: string]: string } = {
        about: '#about',
        projects: '#projects',
        contact: '#contact',
        resume: '#resume',
        home: '/',
      }

      if (section && sections[section]) {
        if (section === 'home') {
          navigate('/')
        } else {
          document.querySelector(sections[section])?.scrollIntoView({ behavior: 'smooth' })
        }
        setOutputs((prev) => [
          ...prev,
          {
            command: cmd,
            output: <div className="text-accent">Navigating to {section}...</div>,
            timestamp: new Date(),
          },
        ])
      } else {
        setOutputs((prev) => [
          ...prev,
          {
            command: cmd,
            output: <div className="text-red-400">Section not found. Use `ls` to see available sections.</div>,
            timestamp: new Date(),
          },
        ])
      }
      return
    }

    if (baseCommand === 'echo') {
      setOutputs((prev) => [
        ...prev,
        {
          command: cmd,
          output: args.join(' '),
          timestamp: new Date(),
        },
      ])
      return
    }

    const commandHandler = commands[baseCommand]

    if (commandHandler) {
      if (commandHandler.clear) {
        setOutputs([])
      } else {
        setOutputs((prev) => [
          ...prev,
          {
            command: cmd,
            output: commandHandler.output,
            timestamp: new Date(),
          },
        ])
      }
    } else {
      setOutputs((prev) => [
        ...prev,
        {
          command: cmd,
          output: <div className="text-red-400">Command not found. Type <span className="text-accent">help</span> for available commands.</div>,
          timestamp: new Date(),
        },
      ])
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentCommand.trim()) return

    setCommandHistory((prev) => [...prev, currentCommand])
    setHistoryIndex(-1)
    executeCommand(currentCommand)
    setCurrentCommand('')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1 
          ? commandHistory.length - 1 
          : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setCurrentCommand(commandHistory[newIndex])
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setCurrentCommand('')
        } else {
          setHistoryIndex(newIndex)
          setCurrentCommand(commandHistory[newIndex])
        }
      }
    }
  }

  return (
    <>
      {/* Terminal Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-50 glass-neu p-2.5 sm:p-3 rounded-xl touch-manipulation"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle Terminal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 sm:w-6 sm:h-6 text-text-primary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </motion.button>

      {/* Terminal Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-20 left-4 right-4 sm:bottom-24 sm:right-6 sm:left-auto z-50 w-auto sm:w-full max-w-[calc(100vw-2rem)] sm:max-w-2xl h-[60vh] sm:h-96 neu rounded-2xl overflow-hidden shadow-2xl"
          >
            {/* Terminal Header */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-[#2A2A2C] dark:bg-[#1A1A1C] border-b border-white/10">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400" />
                <span className="ml-1.5 sm:ml-2 text-xs text-white/70">terminal</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white transition-colors touch-manipulation p-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 sm:w-4 sm:h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Terminal Body */}
            <div className="h-full bg-[#1D1D1F] dark:bg-[#0A0A0B] text-white p-3 sm:p-4 overflow-y-auto font-mono text-xs sm:text-sm">
              <div className="space-y-2">
                {outputs.map((cmd, index) => (
                  <div key={index} className="space-y-1">
                    {cmd.command && (
                      <div>
                        <span className="text-accent">$</span>{' '}
                        <span className="text-white">{cmd.command}</span>
                      </div>
                    )}
                    <div className="text-white/90">{cmd.output}</div>
                  </div>
                ))}
              </div>

              {/* Command Input */}
              <form onSubmit={handleSubmit} className="mt-4 flex items-center gap-2">
                <span className="text-accent text-xs sm:text-sm">$</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={currentCommand}
                  onChange={(e) => setCurrentCommand(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent border-none outline-none text-white placeholder-white/50 text-xs sm:text-sm"
                  placeholder="Type a command..."
                  autoFocus
                />
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="w-2 h-4 bg-accent"
                />
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

